import { inject, Injectable, Injector } from '@angular/core';
import Client from 'fhirclient/lib/Client';
import { firstValueFrom, tap } from 'rxjs';
import { AuthCognitoService } from '../auth/auth-cognito.service';
import { LocationProvider, Patient } from '../contacts/patient.model';
import { PatientService } from '../contacts/patient.service';
import { LogService } from '../core/log.service';
import { IFHIRService } from './fhir-service.interface';
import { ContactInformation } from './model/contact-information';
import { buildStandalonePatientSelfContact } from './standalone-patient-contact';

@Injectable()
/**
 * @class StandaloneService
 * @implements {IFHIRService}
 * @description This service provides various methods to interact with patient and user data.
 * It is designed for compatibility purposes and includes methods to get client, patient, user ID,
 * and practitioner contact information.
 */
export class StandaloneService implements IFHIRService {
  #injector = inject(Injector);
  #authCognitoService = inject(AuthCognitoService);

  private client?: Client;
  private log: LogService = inject(LogService);
  private patient!: Patient;
  
  // Lazy-loaded to break circular dependency
  private get patientService(): PatientService {
    return this.#injector.get(PatientService);
  }

  /**
   * @method getClient
   * @description Retrieves the client information.
   * @returns {Promise<Client | unknown>} A promise that resolves to the client information.
   */
  async getClient(): Promise<Client | unknown> {
    const client = {
      user: {
        id: this.#authCognitoService.getUserId(),
      },
      state: {
        tokenResponse: { location: 'locationId' },
      },
    };
    this.client = client as unknown as Client;
    return this.client;
  }

  /**
   * @method getPatient
   * @description Retrieves the patient information based on the patient ID stored in session storage.
   * @returns {Promise<Patient | undefined>} A promise that resolves to the patient information.
   */
  async getPatient(): Promise<Patient | undefined> {
    const patientId = sessionStorage.getItem('patientId');
    if (this.patient && patientId === this.patient.id) {
      return this.patient;
    }
    return firstValueFrom(
      this.patientService
        .getPatient(patientId || '')
        .pipe(tap((patient) => (this.patient = patient))),
    ) as unknown as Promise<Patient>;
  }

  /** Clears cached patient so the next getPatient() refetches from the API. */
  clearPatientCache(): void {
    this.patient = undefined as unknown as Patient;
  }

  /**
   * @method getEncounterId
   * @description Retrieves the encounter ID. Currently, it returns undefined.
   * @returns {Promise<string | undefined | null>} A promise that resolves to the encounter ID.
   */
  async getEncounterId(): Promise<string | undefined | null> {
    return Promise.resolve(undefined);
  }

  /**
   * @method getPatientContact
   * @description Patient's own phone from the org patient record (header display and alert-patient flow).
   */
  async getPatientContact(): Promise<ContactInformation[]> {
    const contact = await this.resolvePatientSelfContact();
    return contact ? [contact] : [];
  }

  /**
   * @method getUserId
   * @description Retrieves the user ID from the AuthCognitoService.
   * @returns {Promise<string | null | undefined>} A promise that resolves to the user ID.
   */
  async getUserId(): Promise<string | null | undefined> {
    return Promise.resolve(this.#authCognitoService.getUserId());
  }

  /**
   * @method searchLocation
   * @description Searches for the location based on the location ID. It retrieves the patient information and returns the location.
   * @param {string} [locationId] - The ID of the location to search for.
   * @returns {Promise<any>} A promise that resolves to the location information.
   */
  async searchLocation(locationId?: string): Promise<any> {
    const patient = await this.getPatient();
    return patient?.location;
  }

  /**
   * @method searchPractitionerRole
   * @description Searches for the practitioner role based on the provider ID. Currently, it throws an error as the method is not implemented.
   * @param {string} providerId - The ID of the provider to search for.
   * @returns {Promise<any>} A promise that resolves to the practitioner role information.
   */
  searchPractitionerRole(providerId: string): Promise<any> {
    throw new Error('Method not implemented.');
  }

  /**
   * @method getPatientEmergencyContactsInformation
   * @description Seeds the Contacts list from `Patient.phone` (merged/persisted on dashboard load).
   */
  public async getPatientEmergencyContactsInformation(): Promise<ContactInformation[]> {
    const contact = await this.resolvePatientSelfContact();
    return contact ? [contact] : [];
  }

  private async resolvePatientSelfContact(): Promise<ContactInformation | null> {
    const patient = await this.getPatient();
    if (!patient) {
      return null;
    }
    return buildStandalonePatientSelfContact(patient);
  }

  /**
   * @method getPractitionerContactsInformation
   * @description Retrieves the contact information for the practitioner based on the provider ID.
   * @param {string} providerId - The ID of the provider to retrieve contact information for.
   * @returns {Promise<ContactInformation[]>} A promise that resolves to an array of practitioner contact information.
   */
  public async getPractitionerContactsInformation(
    providerId: string,
  ): Promise<ContactInformation[]> {
    const location: LocationProvider = await this.searchLocation();
    try {
      if (!location || !location.id || !location.phone) {
        return [];
      }

      return [
        {
          entityId: providerId,
          locationId: location.id,
          name: location?.name,
          contactNumber: `+1${location.phone}`,
          createdOn: Date.now(),
          isPatient: false,
          fromEpic: true,
        },
      ];
    } catch (e: any) {
      this.log.error(e.message, e);
      return [];
    }
  }
}
